import React, { useCallback, useEffect, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import Modal from "react-modal";
import { db } from "../firebaseConfig";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";
import "../css/PrivateEvents.css";
import { CALENDAR_PACKAGE_OPTIONS, describePackage, findPartyPackage } from "../constants/packageDefaults";
import { applyPackageToNewBooking, bookingPricingUpdate, calculateGuestCharges, createPricingSnapshot, packageEndTime } from "../utils/bookingPricing";
import { calculateBookingCharges, formatCurrency } from "../utils/bookingCalculations";

const PACKAGE_COLORS = {
  "Package 1": { bg: "#0ea5e9", border: "#0284c7" },
  "Package 2": { bg: "#22c55e", border: "#16a34a" },
  "Package 3": { bg: "#a78bfa", border: "#7c3aed" },
  "Package 4": { bg: "#f59e0b", border: "#d97706" },
  "Non-Private Party 1": { bg: "#ef4444", border: "#b91c1c" },
  "Non-Private Party 2": { bg: "#f97316", border: "#c2410c" },
  "Commercial Use": { bg: "#64748b", border: "#475569" },
  default: { bg: "#3499c8", border: "#1e7cb8" },
};

const getPkgColors = (pkg) => PACKAGE_COLORS[pkg] || PACKAGE_COLORS.default;

// NEW: local date formatter to avoid UTC shifts
const toYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const PrivateEvents = ({
  prefill,
  onPrefillConsumed,
  currentUser,
  onGenerateInvoice,
  onGenerateDepositConfirmation,
}) => {
  const [events, setEvents] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedCalendarBooking, setSelectedCalendarBooking] = useState(null);
  const [selectedEventMeta, setSelectedEventMeta] = useState(null); // quick display under calendar
  const [error, setError] = useState("");
  const [pendingPrefill, setPendingPrefill] = useState(null);
  const [activeLeadId, setActiveLeadId] = useState(null);
  const [adoptCurrentPricing, setAdoptCurrentPricing] = useState(false);
  const [visibleYear, setVisibleYear] = useState(new Date().getFullYear());
  const eventsCacheRef = useRef(new Map());

  // --- To-Dos state (per selected event) ---
  const [todos, setTodos] = useState([]);
  const [newTodoTitle, setNewTodoTitle] = useState("");

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    startTime: "10:00",
    endTime: "12:00",
    numberOfKids: "",
    numberOfGuests: "",
    packageType: "",
    message: "",
    deposit: "No",
    depositAmount: 0,
  });

  const packageOptions = CALENDAR_PACKAGE_OPTIONS;
  const displayedPackage = formData.pricingSnapshot?.package;
  const guestCharges = calculateGuestCharges(displayedPackage, formData.numberOfKids, formData.numberOfGuests);
  const estimatedCharges = calculateBookingCharges({ baseSubtotal: displayedPackage?.price, additionalCharges: guestCharges.total });

  // --------- Load events ----------
  const mapEventsForCalendar = useCallback((fetched) => {
    return fetched.map((e) => {
      const colors = getPkgColors(e.packageType);
      return {
        id: e.id,
        title: `${e.firstName} ${e.lastName}`.trim(),
        start: e.startTime,
        end: e.endTime,
        backgroundColor: colors.bg,
        borderColor: colors.border,
        textColor: "#fff",
        display: "block",
        extendedProps: e,
      };
    });
  }, []);

  const fetchEventsForYear = useCallback(
    async (year) => {
      if (eventsCacheRef.current.has(year)) {
        setEvents(mapEventsForCalendar(eventsCacheRef.current.get(year)));
        return;
      }

      const eventsRef = query(
        collection(db, "privateEvents"),
        where("year", "==", year)
      );
      const querySnapshot = await getDocs(eventsRef);
      const fetched = querySnapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      eventsCacheRef.current.set(year, fetched);
      setEvents(mapEventsForCalendar(fetched));
    },
    [mapEventsForCalendar]
  );

  useEffect(() => {
    fetchEventsForYear(visibleYear);
  }, [fetchEventsForYear, visibleYear]);

  // If a lead is sent from the "Event Notes" tab, keep it ready and let the user click a date.
  useEffect(() => {
    if (!prefill) return;

    // If the lead already has a date, open booking immediately (no manual date click).
    if (prefill.eventDate) {
      setAdoptCurrentPricing(false);
      setSelectedEventId(null);
      setSelectedCalendarBooking(null);
      setSelectedEventMeta(null);
      setSelectedDate(prefill.eventDate);
      setFormData(applyPackageToNewBooking({
        firstName: prefill.firstName || "",
        lastName: prefill.lastName || "",
        phoneNumber: prefill.phoneNumber || "",
        email: prefill.email || "",
        startTime: prefill.startTime || "10:00",
        endTime: prefill.endTime || "12:00",
        numberOfKids: "",
        numberOfGuests: "",
        deposit: "No",
        depositAmount: 0,
        message: prefill.message || "",
      }, prefill.packageType || ""));
      setActiveLeadId(prefill.leadId || null);
      setPendingPrefill(null);
      setIsModalOpen(true);
      setError("");
      if (typeof onPrefillConsumed === "function") onPrefillConsumed();

      // highlight chosen day
      requestAnimationFrame(() => {
        document.querySelectorAll(".fc-daygrid-day").forEach((el) => {
          el.classList.toggle(
            "selected-day",
            el.getAttribute("data-date") === prefill.eventDate
          );
        });
      });
      return;
    }

    setPendingPrefill(prefill);
  }, [prefill, onPrefillConsumed]);

  // --------- Load To-Dos when an existing event is selected ----------
  useEffect(() => {
    const loadTodos = async () => {
      if (!selectedEventId) {
        setTodos([]);
        return;
      }
      try {
        const snap = await getDocs(
          collection(db, "privateEvents", selectedEventId, "todos")
        );
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Sort: undone first, then by title
        list.sort(
          (a, b) =>
            Number(a.done) - Number(b.done) || a.title.localeCompare(b.title)
        );
        setTodos(list);
      } catch (e) {
        console.error("Failed loading todos:", e);
      }
    };
    loadTodos();
  }, [selectedEventId]);

  const formatTime = (date) =>
    date
      .toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
      .padStart(5, "0");

  const handleDateClick = (info) => {
    setAdoptCurrentPricing(false);
    setSelectedEventId(null);
    setSelectedCalendarBooking(null);
    setSelectedEventMeta(null);
    setSelectedDate(info.dateStr);
    setFormData({
      firstName: "",
      lastName: "",
      phoneNumber: "",
      email: "",
      startTime: "10:00",
      endTime: "12:00",
      numberOfKids: "",
      numberOfGuests: "",
      packageType: "",
      message: "",
      deposit: "No",
      depositAmount: 0,
      pricingSnapshot: null,
    });

    if (pendingPrefill) {
      setFormData((d) => ({
        ...applyPackageToNewBooking(d, pendingPrefill.packageType || ""),
        firstName: pendingPrefill.firstName || "",
        lastName: pendingPrefill.lastName || "",
        phoneNumber: pendingPrefill.phoneNumber || "",
        email: pendingPrefill.email || "",
        message: pendingPrefill.message || "",
      }));
      setActiveLeadId(pendingPrefill.leadId || null);
      setPendingPrefill(null);
      if (typeof onPrefillConsumed === "function") onPrefillConsumed();
    } else {
      setActiveLeadId(null);
    }
    setIsModalOpen(true);
    setError("");

    // highlight clicked day
    requestAnimationFrame(() => {
      document.querySelectorAll(".fc-daygrid-day").forEach((el) => {
        el.classList.toggle(
          "selected-day",
          el.getAttribute("data-date") === info.dateStr
        );
      });
    });
  };

  const handleEventClick = ({ event }) => {
    setAdoptCurrentPricing(false);
    setActiveLeadId(null);
    setPendingPrefill(null);
    setSelectedEventId(event.id);
    const { extendedProps, start, end } = event;
    setSelectedCalendarBooking({ id: event.id, ...extendedProps });

    // CHANGED: use local date instead of UTC ISO to avoid day shift
    setSelectedDate(toYMD(start));
    setFormData({
      firstName: extendedProps.firstName || "",
      lastName: extendedProps.lastName || "",
      phoneNumber: extendedProps.phoneNumber || "",
      email: extendedProps.email || "",
      startTime: formatTime(new Date(start)),
      endTime: formatTime(new Date(end)),
      numberOfKids: extendedProps.numberOfKids || "",
      numberOfGuests: extendedProps.numberOfGuests || "",
      packageType: extendedProps.packageType || "",
      message: extendedProps.message || "",
      deposit: extendedProps.deposit || "No",
      depositAmount: extendedProps.depositAmount || 0,
      pricingSnapshot: extendedProps.pricingSnapshot || null,
    });
    setSelectedEventMeta({
      title: `${extendedProps.firstName || ""} ${
        extendedProps.lastName || ""
      }`.trim(),
      // CHANGED: use local date string here too
      date: toYMD(start),
      times: `${formatTime(new Date(start))} – ${formatTime(new Date(end))}`,
      packageType: extendedProps.packageType || "",
    });
    setIsModalOpen(true);
    setError("");
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setError("");
    if (!selectedEventId) setActiveLeadId(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((d) => {
      if (name === "packageType") {
        if (!selectedEventId) return applyPackageToNewBooking(d, value);
        // Changing the package is an explicit pricing edit; choosing the original
        // package again restores its saved agreement until "Apply" is clicked.
        const pricingSnapshot = value === selectedCalendarBooking?.packageType && !adoptCurrentPricing
          ? selectedCalendarBooking?.pricingSnapshot || null : createPricingSnapshot(value);
        return { ...d, packageType: value, pricingSnapshot };
      }
      const updated = { ...d, [name]: value };
      if (!selectedEventId && name === "startTime") {
        updated.endTime = packageEndTime(value, d.pricingSnapshot?.package?.durationHours) || d.endTime;
      }
      return updated;
    });
  };

  const applyCurrentPricing = () => {
    setAdoptCurrentPricing(true);
    setFormData((d) => ({ ...d, pricingSnapshot: createPricingSnapshot(d.packageType) }));
  };

  const hasTimeConflict = (startTime, endTime) => {
    return events.some((ev) => {
      if (ev.id === selectedEventId) return false;
      const evDate = ev.start.split("T")[0];
      if (evDate !== selectedDate) return false;

      const evStart = ev.start.split("T")[1];
      const evEnd = ev.end.split("T")[1];
      return (
        (startTime >= evStart && startTime < evEnd) ||
        (endTime > evStart && endTime <= evEnd) ||
        (startTime <= evStart && endTime >= evEnd)
      );
    });
  };

  const resetForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      phoneNumber: "",
      email: "",
      startTime: "10:00",
      endTime: "12:00",
      numberOfKids: "",
      numberOfGuests: "",
      packageType: "",
      message: "",
      deposit: "No",
      depositAmount: 0,
      pricingSnapshot: null,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { startTime, endTime } = formData;

    if (hasTimeConflict(startTime, endTime)) {
      setError("The selected time conflicts with an existing booking.");
      return;
    }

    const {
      firstName,
      lastName,
      phoneNumber,
      email,
      numberOfKids,
      numberOfGuests,
      packageType,
      message,
      deposit,
      depositAmount,
    } = formData;

    const eventDate = new Date(selectedDate);
    const eventData = {
      firstName,
      lastName,
      phoneNumber,
      email,
      startTime: `${selectedDate}T${startTime}`,
      endTime: `${selectedDate}T${endTime}`,
      numberOfKids: parseInt(numberOfKids || 0, 10),
      numberOfGuests: parseInt(numberOfGuests || 0, 10),
      packageType,
      message,
      deposit,
      depositAmount: parseFloat(depositAmount || 0),
      month: eventDate.getMonth() + 1,
      year: eventDate.getFullYear(),
      ...bookingPricingUpdate(selectedEventId ? selectedCalendarBooking : null, packageType, adoptCurrentPricing),
    };

    try {
      if (selectedEventId) {
        await updateDoc(doc(db, "privateEvents", selectedEventId), {
          ...eventData,
          updatedAt: serverTimestamp(),
          updatedByUserId: currentUser?.id || "",
          updatedByUsername: currentUser?.username || "",
          updatedByRole: currentUser?.role || "",
        });

        const colors = getPkgColors(packageType);
        setEvents((prev) =>
          prev.map((ev) =>
            ev.id === selectedEventId
              ? {
                  ...ev,
                  title: `${firstName} ${lastName}`.trim(),
                  start: eventData.startTime,
                  end: eventData.endTime,
                  backgroundColor: colors.bg,
                  borderColor: colors.border,
                  textColor: "#fff",
                  display: "block",
                  extendedProps: { ...ev.extendedProps, ...eventData },
                }
              : ev
          )
        );
        // update the meta shown under calendar (if panel visible after close/open)
        setSelectedEventMeta({
          title: `${firstName} ${lastName}`.trim(),
          date: selectedDate,
          times: `${startTime} – ${endTime}`,
          packageType,
        });
        setSelectedCalendarBooking((previous) => ({
          ...previous,
          id: selectedEventId,
          ...eventData,
        }));
      } else {
        const docRef = await addDoc(collection(db, "privateEvents"), {
          ...eventData,
          createdAt: serverTimestamp(),
          createdByUserId: currentUser?.id || "",
          createdByUsername: currentUser?.username || "",
          createdByRole: currentUser?.role || "",
        });
        const colors = getPkgColors(packageType);
        setEvents((prev) => [
          ...prev,
          {
            id: docRef.id,
            title: `${firstName} ${lastName}`.trim(),
            start: eventData.startTime,
            end: eventData.endTime,
            backgroundColor: colors.bg,
            borderColor: colors.border,
            textColor: "#fff",
            display: "block",
            extendedProps: {
              ...eventData,
              createdByUserId: currentUser?.id || "",
              createdByUsername: currentUser?.username || "",
              createdByRole: currentUser?.role || "",
            },
          },
        ]);

        // If this booking came from a lead, mark the lead as booked and link it.
        if (activeLeadId) {
          try {
            await updateDoc(doc(db, "privateEventLeads", activeLeadId), {
              status: "booked",
              privateEventId: docRef.id,
              bookedAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            });
          } catch (e2) {
            console.error("Failed to link lead to booking:", e2);
          } finally {
            setActiveLeadId(null);
          }
        }
        // New event added: if you want to immediately show its todos panel, uncomment:
        // setSelectedEventId(docRef.id);
        // setSelectedEventMeta({
        //   title: `${firstName} ${lastName}`.trim(),
        //   date: selectedDate,
        //   times: `${startTime} – ${endTime}`,
        //   packageType,
        // });
      }

      eventsCacheRef.current.clear();
      closeModal();
      resetForm();
      setError("");
    } catch (err) {
      console.error("Error saving event:", err);
    }
  };

  const handleDelete = async () => {
    if (!selectedEventId) return;
    const ok = window.confirm(
      "Delete this booking? This action cannot be undone."
    );
    if (!ok) return;

    try {
      await deleteDoc(doc(db, "privateEvents", selectedEventId));
      setEvents((prev) => prev.filter((ev) => ev.id !== selectedEventId));
      setIsModalOpen(false);
      resetForm();
      setSelectedEventId(null);
      setSelectedCalendarBooking(null);
      setSelectedEventMeta(null);
      setTodos([]);
    } catch (err) {
      console.error("Error deleting event:", err);
    }
  };

  const handleGenerateInvoice = () => {
    if (!selectedCalendarBooking || typeof onGenerateInvoice !== "function") return;
    onGenerateInvoice(selectedCalendarBooking);
  };

  const handleGenerateDepositConfirmation = () => {
    if (
      !selectedCalendarBooking ||
      typeof onGenerateDepositConfirmation !== "function"
    ) {
      return;
    }
    onGenerateDepositConfirmation(selectedCalendarBooking);
  };

  // --------- To-Dos handlers ----------
  const addTodo = async (e) => {
    e.preventDefault();
    if (!selectedEventId || !newTodoTitle.trim()) return;
    try {
      const ref = await addDoc(
        collection(db, "privateEvents", selectedEventId, "todos"),
        {
          title: newTodoTitle.trim(),
          done: false,
        }
      );
      setTodos((prev) => [
        ...prev,
        { id: ref.id, title: newTodoTitle.trim(), done: false },
      ]);
      setNewTodoTitle("");
    } catch (err) {
      console.error("Failed to add todo:", err);
    }
  };

  const toggleTodo = async (todo) => {
    try {
      await updateDoc(
        doc(db, "privateEvents", selectedEventId, "todos", todo.id),
        { done: !todo.done }
      );
      setTodos((prev) =>
        prev.map((t) => (t.id === todo.id ? { ...t, done: !t.done } : t))
      );
    } catch (err) {
      console.error("Failed to toggle todo:", err);
    }
  };

  const deleteTodo = async (todoId) => {
    try {
      await deleteDoc(
        doc(db, "privateEvents", selectedEventId, "todos", todoId)
      );
      setTodos((prev) => prev.filter((t) => t.id !== todoId));
    } catch (err) {
      console.error("Failed to delete todo:", err);
    }
  };

  return (
    <div className="private-events">
      <h2>Event Calendar</h2>

      {pendingPrefill && (
        <div className="prefill-banner" role="status">
          Lead loaded:{" "}
          <strong>
            {`${pendingPrefill.firstName || ""} ${
              pendingPrefill.lastName || ""
            }`.trim() || "Unnamed"}
          </strong>
          {pendingPrefill.phoneNumber ? ` • ${pendingPrefill.phoneNumber}` : ""}
          . Click a date on the calendar to book and it will auto-fill the form.
        </div>
      )}

      {/* Package color legend (top-right) */}
      <div className="pkg-legend">
        <div className="pkg-legend-title">Package Colors</div>
        {Object.entries(PACKAGE_COLORS)
          .filter(([k]) => k !== "default")
          .map(([name, colors]) => (
            <div className="pkg-legend-row" key={name}>
              <span
                className="pkg-dot"
                style={{ background: colors.bg, borderColor: colors.border }}
              />
              <span className="pkg-name">{name}</span>
            </div>
          ))}
      </div>

      <div className="calendar-scroll-wrapper">
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "dayGridMonth,timeGridWeek,timeGridDay",
          }}
          height="auto" // prevent clipping
          expandRows={true} // keep full weeks in view
          dayMaxEventRows={3} // show +N more link
          eventDisplay="block" // ensure block in month
          handleWindowResize={true}
          selectable
          events={events}
          eventContent={(info) => {
            const isPast = new Date(info.event.end) < new Date();
            return (
              <div
                className={`event-chip ${isPast ? "event-past" : ""}`}
                title={`${info.event.title}`}
              >
                <span className="event-time">
                  {new Date(info.event.start).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                  —
                  {new Date(info.event.end).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
                <span className="event-title">{info.event.title}</span>
              </div>
            );
          }}
          dateClick={handleDateClick}
          datesSet={(info) => {
            setVisibleYear(info.view.currentStart.getFullYear());
          }}
          eventClick={handleEventClick}
          timeZone="local" // NEW: ensure calendar uses local timezone
        />
      </div>

      {/* Per-event To-Dos panel (below calendar) */}
      {selectedEventId && (
        <div className="todos-panel">
          <div className="todos-header">
            <div className="todos-title">
              <strong>To-Dos</strong>
              {selectedEventMeta && (
                <span className="todos-event-meta">
                  • {selectedEventMeta.title} • {selectedEventMeta.date} •{" "}
                  {selectedEventMeta.times} •{" "}
                  <span className="todos-package">
                    {selectedEventMeta.packageType}
                  </span>
                </span>
              )}
            </div>
            <form className="todos-add" onSubmit={addTodo}>
              <input
                type="text"
                placeholder="Add a prep task…"
                value={newTodoTitle}
                onChange={(e) => setNewTodoTitle(e.target.value)}
              />
              <button type="submit">Add</button>
            </form>
          </div>

          <div className="todos-list">
            {todos.length === 0 ? (
              <div className="todos-empty">
                No tasks yet. Add your first one!
              </div>
            ) : (
              todos.map((t) => (
                <div
                  key={t.id}
                  className={`todo-item ${t.done ? "todo-done" : "todo-open"}`}
                >
                  <button
                    className={`todo-status ${t.done ? "ok" : "no"}`}
                    title={t.done ? "Done" : "Not done"}
                    onClick={() => toggleTodo(t)}
                  >
                    {t.done ? "✓" : "!"}
                  </button>
                  <div className="todo-title">{t.title}</div>
                  <button
                    className="todo-delete"
                    title="Delete"
                    onClick={() => deleteTodo(t.id)}
                  >
                    🗑
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Create/Edit modal */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        className="booking-modal"
        overlayClassName="booking-modal-overlay"
        ariaHideApp={false}
      >
        <div className="modal-header">
          <h2>{selectedEventId ? "Edit Event" : "Book Your Event"}</h2>
          <button
            type="button"
            className="modal-close"
            onClick={closeModal}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-grid">
          {error && <p className="error-message">{error}</p>}

          {["firstName", "lastName", "phoneNumber", "email"].map((field) => (
            <div className="form-group" key={field}>
              <label>
                {field
                  .replace(/([A-Z])/g, " $1")
                  .replace(/^./, (c) => c.toUpperCase())}
              </label>
              <input
                type={field === "email" ? "email" : "text"}
                name={field}
                value={formData[field]}
                onChange={handleInputChange}
                required
              />
            </div>
          ))}

          {["startTime", "endTime"].map((field) => (
            <div className="form-group" key={field}>
              <label>{field === "startTime" ? "Start Time" : "End Time"}</label>
              <input
                type="time"
                name={field}
                value={formData[field]}
                onChange={handleInputChange}
                required
              />
            </div>
          ))}

          {["numberOfKids", "numberOfGuests"].map((field) => (
            <div className="form-group" key={field}>
              <label>
                {field === "numberOfKids"
                  ? "Number of Kids"
                  : "Number of Adults (Guests)"}
              </label>
              <input
                type="number"
                name={field}
                value={formData[field]}
                onChange={handleInputChange}
                required
                min="0"
              />
            </div>
          ))}

          <div className="form-group">
            <label>Package Type</label>
            <select
              name="packageType"
              value={formData.packageType}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Package</option>
              {packageOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {findPartyPackage(opt)?.name || opt}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group form-group-full">
            {displayedPackage ? (
              <>
                <p>{describePackage(displayedPackage)}</p>
                <p>Package: {formatCurrency(displayedPackage.price)}.
                  {" "}Additional child: {formatCurrency(displayedPackage.additionalChildPrice)};
                  {" "}additional adult: {formatCurrency(displayedPackage.additionalAdultPrice)}.</p>
                <p>Guest charges: {formatCurrency(guestCharges.total)}
                  {" "}({guestCharges.additionalKids} extra kids, {guestCharges.additionalAdults} extra adults).
                  {" "}Estimate with 10.25% tax: {formatCurrency(estimatedCharges.finalEventTotal)}.</p>
                <p>Estimate excludes separately agreed add-ons, discounts and payments.</p>
              </>
            ) : selectedEventId ? (
              <p>Agreed pricing is not stored for this booking. Its existing financial records are preserved. Review the original agreement when generating documents.</p>
            ) : null}
            {selectedEventId && (
              <>
                <p>Changing the package or applying current pricing explicitly replaces this booking’s package defaults. Saving other fields preserves its pricing.</p>
                <button type="button" onClick={applyCurrentPricing} disabled={!findPartyPackage(formData.packageType)}>
                  Apply current package pricing
                </button>
              </>
            )}
          </div>

          <div className="form-group form-group-full">
            <label>Message</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              rows={3}
            />
          </div>

          <div className="form-group">
            <label>Deposit</label>
            <select
              name="deposit"
              value={formData.deposit}
              onChange={handleInputChange}
              required
            >
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>

          <div className="form-group">
            <label>Deposit Amount</label>
            <input
              type="number"
              name="depositAmount"
              value={formData.depositAmount}
              onChange={handleInputChange}
              disabled={formData.deposit === "No"}
              required={formData.deposit === "Yes"}
              min="0"
              step="0.01"
            />
          </div>

          <div className="modal-actions">
            <button type="submit" className="submit-button">
              {selectedEventId ? "Update Booking" : "Submit Booking"}
            </button>
            {selectedEventId && (
              <>
                <button
                  type="button"
                  className="document-button"
                  onClick={handleGenerateInvoice}
                >
                  Generate Invoice
                </button>
                <button
                  type="button"
                  className="document-button document-button-secondary"
                  onClick={handleGenerateDepositConfirmation}
                >
                  Generate Deposit Confirmation
                </button>
              </>
            )}
            {selectedEventId && (
              <button
                type="button"
                className="delete-button"
                onClick={handleDelete}
              >
                Delete Booking
              </button>
            )}
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default PrivateEvents;
// i am trying to ciommint somethingggggggg
